import React from 'react';
import { Modal, Switch } from '@mantine/core';
import { Button } from '../../../../components/Button/Button';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import { PosWheelTimePicker } from './PosWheelTimePicker';
import { useAutoCloseSessionController } from './useAutoCloseSessionController';
import classes from './auto-close-session-modal.module.scss';

export type AutoCloseSessionModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  autoCloseTime: string | null;
  sessionOpenTime: string | null;
  onSaved: (next: string | null) => void;
};

// Manager modal to set/clear the per-tenant session auto-close time via a wheel picker.
const AutoCloseSessionModalComponent: React.FC<AutoCloseSessionModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  autoCloseTime,
  sessionOpenTime,
  onSaved,
}) => {
  const { enabled, setEnabled, time, setTime, isDirty, isSaving, error, nextFire, openSince, handleSave } =
    useAutoCloseSessionController({
      opened,
      tenantId,
      employeeSession,
      autoCloseTime,
      sessionOpenTime,
      onSaved,
      onClose,
    });

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!isSaving) {
          onClose();
        }
      }}
      onContextMenu={(event) => event.preventDefault()}
      title={<span className={classes.title}>סגירה אוטומטית של הקופה</span>}
      size="md"
      centered
      radius="md"
      classNames={{ root: navyModal.root }}
      overlayProps={{ opacity: 0.55, blur: 2 }}
      closeButtonProps={{ 'data-testid': 'auto-close-close' } as Record<string, string>}
    >
      <div className={classes.body} dir="rtl">
        <Switch
          data-testid="auto-close-enabled"
          checked={enabled}
          onChange={(event) => setEnabled(event.currentTarget.checked)}
          label="הפעל סגירה אוטומטית"
          disabled={isSaving}
        />

        {enabled && (
          <div className={classes.timeRow}>
            <PosWheelTimePicker value={time} onChange={setTime} disabled={isSaving} />
          </div>
        )}

        <dl className={classes.info}>
          <div className={classes.infoRow}>
            <dt>סגירה אוטומטית הבאה</dt>
            <dd>{enabled ? nextFire : 'כבוי'}</dd>
          </div>
          <div className={classes.infoRow}>
            <dt>המשמרת פתוחה מאז</dt>
            <dd>{openSince}</dd>
          </div>
        </dl>

        {error && <p className={classes.error}>{error}</p>}

        <div className={classes.actions}>
          <Button variant="cancel" onClick={onClose} disabled={isSaving} dataTestId="auto-close-cancel">
            ביטול
          </Button>
          <Button variant="save" onClick={handleSave} disabled={!isDirty || isSaving} loading={isSaving} dataTestId="auto-close-save">
            שמור
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const AutoCloseSessionModal = React.memo(AutoCloseSessionModalComponent);
